import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { UserProfileSheet } from "./UserProfileSheet";

interface Ctx {
  open: (userId: number) => void;
  close: () => void;
}

const UserProfileSheetCtx = createContext<Ctx | null>(null);

/**
 * Mount once at the app root. Any component can open the profile sheet
 * via `useOpenUserProfile()` — we keep the Sheet DOM node centralized so
 * there's only one, avoids animation/state conflicts.
 */
export function UserProfileSheetProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<number | null>(null);

  const open = useCallback((id: number) => setUserId(id), []);
  const close = useCallback(() => setUserId(null), []);

  const value = useMemo<Ctx>(() => ({ open, close }), [open, close]);

  return (
    <UserProfileSheetCtx.Provider value={value}>
      {children}
      <UserProfileSheet userId={userId} onClose={close} />
    </UserProfileSheetCtx.Provider>
  );
}

export function useOpenUserProfile(): (userId: number) => void {
  const ctx = useContext(UserProfileSheetCtx);
  if (!ctx) throw new Error("useOpenUserProfile must be used inside <UserProfileSheetProvider>");
  return ctx.open;
}
